import React from 'react';
import { BoardZiel } from '../../types';
import { wochentagLabel } from '../../lib/board';
import { navigiere } from '../../lib/navigation';
import { zielErreichbar } from '../../lib/tabRollen';

const NICHT_ERREICHBAR = 'In Ihrer Rolle nicht erreichbar';

export interface WochenEinsatz {
  einsatz_id: number;
  mitarbeiter: string;
  text: string;
  auftrag_id: number | null;
}

export interface WochenUngeplant {
  auftrag_id: number;
  auftrag_nr?: string;
  text: string;
}

export interface WochenTag {
  datum: string;
  einsaetze: WochenEinsatz[];
  ungeplant: WochenUngeplant[];
  frei: string[];
}

interface Props {
  tage: WochenTag[];
  /** Ziel für Einsätze ohne Auftrag (i. d. R. das Ziel der Kachel: Einsatzplanung). */
  ziel?: BoardZiel | null;
  /** Verwaltungs-Tabs, die der Benutzer sieht (null: Rollen noch nicht geladen). */
  erreichbareTabs: string[] | null;
}

function auftragZiel(auftragId: number): BoardZiel {
  return { ansicht: 'management', tab: 'auftraege', params: { auftrag_id: auftragId } };
}

/** Wochenplan Mo–Fr: je Tag die Einsätze, rot markiert die noch ungeplanten
 *  Aufträge mit Termin an dem Tag, darunter die freien Mitarbeiter. */
const Wochenplan: React.FC<Props> = ({ tage, ziel, erreichbareTabs }) => {
  // Auftragsakte und Kachel-Ziel nur klickbar, wenn die Rolle den Tab sieht.
  const auftragErreichbar = zielErreichbar(auftragZiel(0), erreichbareTabs);
  const kachelZielErreichbar = !!ziel && zielErreichbar(ziel, erreichbareTabs);
  return (
  <div className="k-wochenplan">
    {tage.map(tag => (
      <section key={tag.datum} className="k-wochenplan__tag">
        <h4 className="k-wochenplan__datum">{wochentagLabel(tag.datum)}</h4>
        {tag.einsaetze.length === 0 && tag.ungeplant.length === 0 && (
          <p className="k-wochenplan__leer">Keine Einsätze</p>
        )}
        <ul className="k-wochenplan__liste">
          {tag.einsaetze.map(e => {
            const inhalt = (
              <>
                <span className="k-wochenplan__wer">{e.mitarbeiter || 'Ohne Zuordnung'}</span>
                <span className="k-wochenplan__was">{e.text}</span>
              </>
            );
            const auftragId = e.auftrag_id;
            return (
              <li key={e.einsatz_id}>
                {auftragId !== null && auftragErreichbar ? (
                  <button type="button" className="k-wochenplan__einsatz" onClick={() => navigiere(auftragZiel(auftragId))}>
                    {inhalt}
                  </button>
                ) : auftragId === null && ziel && kachelZielErreichbar ? (
                  <button type="button" className="k-wochenplan__einsatz" onClick={() => navigiere(ziel)}>
                    {inhalt}
                  </button>
                ) : (
                  <div
                    className="k-wochenplan__einsatz"
                    title={auftragId !== null || ziel ? NICHT_ERREICHBAR : undefined}
                  >
                    {inhalt}
                  </div>
                )}
              </li>
            );
          })}
          {tag.ungeplant.map(u => {
            const inhalt = (
              <>
                <span className="k-wochenplan__marker">ungeplant</span>
                <span className="k-wochenplan__was">{u.text}</span>
              </>
            );
            return (
              <li key={`u-${u.auftrag_id}`}>
                {auftragErreichbar ? (
                  <button
                    type="button"
                    className="k-wochenplan__einsatz is-ungeplant"
                    onClick={() => navigiere(auftragZiel(u.auftrag_id))}
                  >
                    {inhalt}
                  </button>
                ) : (
                  <div className="k-wochenplan__einsatz is-ungeplant" title={NICHT_ERREICHBAR}>{inhalt}</div>
                )}
              </li>
            );
          })}
        </ul>
        {tag.frei.length > 0 && (
          <p className="k-wochenplan__frei">frei: {tag.frei.join(', ')}</p>
        )}
      </section>
    ))}
  </div>
  );
};

export default Wochenplan;
